import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { NotificationData } from './types';

interface NotificationContextType {
  showNotification: (notification: NotificationData) => void;
  hideNotification: (id: string) => void;
  clearAllNotifications: () => void;
  notifications: NotificationItem[];
}

interface NotificationItem extends NotificationData {
  id: string;
  timestamp: number;
  isVisible: boolean;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const timeoutRefs = useRef<Map<string, NodeJS.Timeout>>(new Map());

  const hideNotification = useCallback((id: string) => {
    setNotifications(prev =>
      prev.map(notification =>
        notification.id === id
          ? { ...notification, isVisible: false }
          : notification
      )
    );

    // Remove from DOM after animation
    setTimeout(() => {
      setNotifications(prev => prev.filter(notification => notification.id !== id));
    }, 300);
  }, []);

  const showNotification = useCallback((notification: NotificationData) => {
    const id = `notification_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const timestamp = Date.now();

    const newNotification: NotificationItem = {
      ...notification,
      id,
      timestamp,
      isVisible: false
    };

    setNotifications(prev => [...prev, newNotification]);

    // Trigger entrance animation
    requestAnimationFrame(() => {
      setNotifications(prev =>
        prev.map(n =>
          n.id === id ? { ...n, isVisible: true } : n
        )
      );
    });

    // Auto-hide if duration is specified
    if (notification.duration && notification.duration > 0) {
      const timeoutId = setTimeout(() => {
        hideNotification(id);
      }, notification.duration);

      timeoutRefs.current.set(id, timeoutId);
    }

    return id;
  }, [hideNotification]);

  const clearAllNotifications = useCallback(() => {
    // Clear all timeouts
    timeoutRefs.current.forEach(timeoutId => {
      clearTimeout(timeoutId);
    });
    timeoutRefs.current.clear();

    // Hide all notifications first for animation
    setNotifications(prev =>
      prev.map(notification => ({ ...notification, isVisible: false }))
    );

    // Remove all after animation
    setTimeout(() => {
      setNotifications([]);
    }, 300);
  }, []);

  // Initialize global notification context
  useEffect(() => {
    const contextValue = {
      showNotification,
      hideNotification,
      clearAllNotifications,
      notifications
    };

    if (typeof window !== 'undefined') {
      (window as any).setNotificationContext(contextValue);
    }
  }, [showNotification, hideNotification, clearAllNotifications, notifications]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      timeoutRefs.current.forEach(timeoutId => {
        clearTimeout(timeoutId);
      });
    };
  }, []);

  const value = {
    showNotification,
    hideNotification,
    clearAllNotifications,
    notifications
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <NotificationCenter notifications={notifications} onHide={hideNotification} />
    </NotificationContext.Provider>
  );
};

interface NotificationCenterProps {
  notifications: NotificationItem[];
  onHide: (id: string) => void;
}

const NotificationCenter: React.FC<NotificationCenterProps> = ({ notifications, onHide }) => {
  if (notifications.length === 0) {
    return null;
  }

  return (
    <div className="fixed top-4 right-4 z-50 space-y-2 max-w-sm">
      {notifications.map((notification) => (
        <NotificationToast
          key={notification.id}
          notification={notification}
          onClose={() => onHide(notification.id)}
        />
      ))}
    </div>
  );
};

interface NotificationToastProps {
  notification: NotificationItem;
  onClose: () => void;
}

const NotificationToast: React.FC<NotificationToastProps> = ({ notification, onClose }) => {
  const getIcon = () => {
    switch (notification.type) {
      case 'error':
        return (
          <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
              clipRule="evenodd"
            />
          </svg>
        );
      case 'warning':
        return (
          <svg className="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
        );
      case 'info':
      default:
        return (
          <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
              clipRule="evenodd"
            />
          </svg>
        );
    }
  };

  const getStyles = () => {
    switch (notification.type) {
      case 'error':
        return 'bg-red-50 border-red-200 text-red-800';
      case 'warning':
        return 'bg-yellow-50 border-yellow-200 text-yellow-800';
      case 'info':
      default:
        return 'bg-blue-50 border-blue-200 text-blue-800';
    }
  };

  const closeButtonId = `close-${notification.id}`;

  return (
    <div
      className={`
        transform transition-all duration-300 ease-in-out
        ${notification.isVisible ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'}
        p-4 rounded-lg border shadow-lg max-w-sm w-full
        ${getStyles()}
      `}
      role="alert"
      aria-live="assertive"
      aria-atomic="true"
    >
      <div className="flex">
        <div className="flex-shrink-0">
          {getIcon()}
        </div>
        <div className="ml-3 flex-1">
          <h3 className="text-sm font-medium">
            {notification.title}
          </h3>
          <div className="mt-1 text-sm">
            {notification.message}
          </div>
          {notification.action && (
            <div className="mt-3">
              <button
                onClick={notification.action.onClick}
                className="text-sm underline hover:no-underline focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 rounded"
              >
                {notification.action.label}
              </button>
            </div>
          )}
        </div>
        <div className="ml-auto pl-3">
          <div className="-mx-1.5 -my-1.5">
            <button
              id={closeButtonId}
              onClick={onClose}
              className="inline-flex rounded-md p-1.5 hover:bg-opacity-20 hover:bg-muted0 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              aria-label="Fechar notificação"
            >
              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Make notification system globally available
export const initializeGlobalNotifications = () => {
  if (typeof window !== 'undefined') {
    let notificationContext: NotificationContextType | null = null;

    // This will be set by the NotificationProvider
    (window as any).setNotificationContext = (context: NotificationContextType) => {
      notificationContext = context;
    };

    (window as any).showNotification = (notification: NotificationData) => {
      if (notificationContext) {
        return notificationContext.showNotification(notification);
      } else {
        console.warn('Notification context not available. Make sure NotificationProvider is wrapping your app.');
      }
    };

    (window as any).hideNotification = (id: string) => {
      if (notificationContext) {
        notificationContext.hideNotification(id);
      }
    };

    (window as any).clearAllNotifications = () => {
      if (notificationContext) {
        notificationContext.clearAllNotifications();
      }
    };
  }
};

// Initialize global notifications when the module loads
initializeGlobalNotifications();